import { deleteRequest, get, post, put } from "@/lib/http";
import { NewUserPayload, User } from "@/types/user.type";
import { toast } from "sonner";
import { proxy, snapshot, useSnapshot } from "valtio";
import { getAccountProxy } from "./account.store";

const accountProxy = getAccountProxy();

export const userProxy = proxy({
  users: [] as User[],
  usersCount: 0,
  isLoading: false,
  isLoadingCount: false,
  limit: 20,
  offset: 0,
  searchQuery: "",

  fetchUsers: async (
    limit: number = userProxy.limit,
    offset: number = userProxy.offset,
    searchQuery: string = userProxy.searchQuery
  ) => {
    userProxy.isLoading = true;
    userProxy.limit = limit;
    userProxy.offset = offset;
    userProxy.searchQuery = searchQuery;

    try {
      const searchParam = searchQuery ? `&search=${searchQuery}` : "";
      const response = await get<User[]>(
        `/api/user/?userId=${accountProxy.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]}&limit=${limit}&offset=${offset}${searchParam}`
      );
      userProxy.users = response;
    } catch (error) {
      console.log("Error fetching users:", error);
      toast.error(`Error fetching users: ${error}`);
    } finally {
      userProxy.isLoading = false;
    }
  },

  fetchUsersCount: async () => {
    userProxy.isLoadingCount = true;
    try {
      const response = await get<{ count: number }>(
        `/api/user/count/${accountProxy.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]}`
      );
      userProxy.usersCount = response.count;
    } catch (error) {
      console.log("Error fetching users count:", error);
      toast.error(`Error fetching users count: ${error}`);
    } finally {
      userProxy.isLoadingCount = false;
    }
  },

  createUser: async (createUserDto: NewUserPayload) => {
    try {
      await post<User, NewUserPayload>(
        `/api/user/${accountProxy.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]}`,
        createUserDto
      );
      userProxy.fetchUsers();
      toast.success("User has been created");
    } catch (error: any) {
      console.log(error);
      toast.error(error.message);
    }
  },

  updateUser: async (updateUserDto: NewUserPayload, userId: string) => {
    try {
      await put<User, NewUserPayload>(`/api/user/${userId}`, updateUserDto);
      userProxy.fetchUsers();
      toast.success("User has been updated");
    } catch (error: any) {
      console.log(error);
      toast.error(error.message);
    }
  },

  deleteUser: async (userId: string | undefined) => {
    try {
      if (!userId)
        throw new Error("Failed to delet user : User ID is undefined");
      await deleteRequest(`/api/user/${userId}`);
      userProxy.fetchUsers();
      toast.success("User successfully deleted");
    } catch (error: any) {
      console.log(error);
      toast.error(error.message);
    }
  }
});

export const getUserSnapshot = () => snapshot(userProxy);
export const getUserProxy = () => userProxy;

export const useUserSnapshot = (options?: Parameters<typeof useSnapshot>[1]) =>
  useSnapshot(userProxy, options);

export const useUserProxy = () => userProxy;

export const useUserStore = (options?: Parameters<typeof useSnapshot>[1]) => {
  return {
    userSnapshot: useSnapshot(userProxy, options),
    userProxy: userProxy
  };
};
